// Transient inspector state the host pieces share: what the pointer is over, "Add another",
// a pending property edit, and the geometry clock. Selection itself is INS's InspectSession;
// nothing here is authority.
import { createContext, useContext } from 'react';
import type { GeoClock } from './geometry';
import type { Candidate, GeoIdentity, InspectAdapter, InspectSession, Rect } from './types';

export type HoverState = { candidate: Candidate; label: string | null } | null;

export type InspectHost = {
  adapter: InspectAdapter;
  session: InspectSession;
  clock: GeoClock;
  /** A new hover or click identity, announced to INS before the surface is asked. */
  /** Null when INS refuses (inactive, rate-limited, stale): the caller does nothing. */
  request: (kind: 'hover' | 'click') => GeoIdentity | null;
  phone: boolean;
  hover: HoverState;
  setHover: (hover: HoverState) => void;
  addMode: boolean;
  setAddMode: (value: boolean) => void;
  editing: boolean;
  setEditing: (value: boolean) => void;
  /** Visible host rects for a selected target, at the current geometry. */
  rectsFor: (targetId: string) => Rect[];
  /** The layer's size, for placing panels. */
  viewport: Rect;
  /** Leave Inspect, clear, or cancel an edit: the Escape order. */
  escape: () => void;
  /**
   * The one place a resolved target joins or leaves the set, for pointer, keyboard and picker
   * alike: replace, add, ⌘-click on a selected target removes it, and nothing past the limit.
   */
  choose: (targetId: string, mode: 'add' | 'replace') => void;
  /** An edit was saved to these targets: their outlines acknowledge it once. */
  markSaved: (targetIds: string[]) => void;
};

export const InspectHostContext = createContext<InspectHost | null>(null);

export function useInspectHost(): InspectHost | null {
  return useContext(InspectHostContext);
}
